import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store';
import { SkillLaunchContext } from './SkillLaunchContext';
import { i18nService } from '../../services/i18n';
import OnlineSkillsPanel from './OnlineSkillsPanel';
import PersonalSkillsPanel from './PersonalSkillsPanel';
import FeaturedSkillsPanel from './FeaturedSkillsPanel';
import SidebarToggleIcon from '../icons/SidebarToggleIcon';
import ComposeIcon from '../icons/ComposeIcon';
import WindowTitleBar from '../window/WindowTitleBar';

interface SkillsViewProps {
  isSidebarCollapsed?: boolean;
  onToggleSidebar?: () => void;
  onNewChat?: () => void;
  /** 注入新对话导航；详情只提交稳定技能 ID 和可选草稿。 */
  onUseSkill?: (skillId: string, question?: string) => boolean;
}

type SkillsTab = 'personal' | 'featured' | 'community';

const SkillsView: React.FC<SkillsViewProps> = ({ isSidebarCollapsed, onToggleSidebar, onNewChat, onUseSkill }) => {
  const skills = useSelector((state: RootState) => state.skill.skills);
  const launchContext = useMemo(() => onUseSkill ? { skills, launch: onUseSkill } : null, [skills, onUseSkill]);
  const isMac = window.electron.platform === 'darwin';
  const [activeTab, setActiveTab] = useState<SkillsTab>('personal');

  const tabs: { key: SkillsTab; label: string }[] = [
    { key: 'personal', label: i18nService.t('skillsLocalTab') },
    { key: 'featured', label: i18nService.t('skillsFeaturedTab') },
    { key: 'community', label: i18nService.t('skillsOnlineTab') },
  ];

  const renderPanel = () => {
    switch (activeTab) {
      case 'personal':
        return <PersonalSkillsPanel />;
      case 'featured':
        return <FeaturedSkillsPanel />;
      case 'community':
        return <OnlineSkillsPanel />;
    }
  };

  return (
    <div className="flex h-full flex-1 flex-col bg-[#FCFCFC] dark:bg-claude-darkBg">
      <div className="draggable flex h-12 shrink-0 items-center justify-between px-4">
        <div className="flex items-center space-x-3 h-8">
          {isSidebarCollapsed && (
            <div className={`non-draggable flex items-center gap-1 ${isMac ? 'pl-[68px]' : ''}`}>
              <button
                type="button"
                onClick={onToggleSidebar}
                className="h-8 w-8 inline-flex items-center justify-center rounded-lg dark:text-claude-darkTextSecondary text-claude-textSecondary hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover transition-colors"
              >
                <SidebarToggleIcon className="h-4 w-4" isCollapsed={true} />
              </button>
              <button
                type="button"
                onClick={onNewChat}
                className="h-8 w-8 inline-flex items-center justify-center rounded-lg dark:text-claude-darkTextSecondary text-claude-textSecondary hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover transition-colors"
              >
                <ComposeIcon className="h-4 w-4" />
              </button>
            </div>
          )}
          <h1 className="text-lg font-semibold text-claude-text dark:text-claude-darkText">
            {i18nService.t('skills')}
          </h1>
        </div>
        <WindowTitleBar inline />
      </div>

      <div className="non-draggable shrink-0">
        <div className="mx-auto flex w-full max-w-6xl gap-6 px-4 pt-1">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`rounded-lg px-1 py-1.5 text-sm font-semibold transition-colors ${
                activeTab === tab.key
                  ? 'text-claude-text dark:text-claude-darkText'
                  : 'text-claude-textSecondary/70 hover:text-claude-text dark:text-claude-darkTextSecondary/70 dark:hover:text-claude-darkText'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-6xl px-4 py-4">
          <SkillLaunchContext.Provider value={launchContext}>{renderPanel()}</SkillLaunchContext.Provider>
        </div>
      </div>
    </div>
  );
};

export default SkillsView;
